import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays, Clock, MapPin, Users, Eye, ChevronRight, EyeOff, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Event, EventSeries } from '../../types/event';
import eventAPI from '../../services/eventAPI';
import Button from '../UI/Button';
import { generateEventViewUrl } from '../../utils/urlUtils';
import { useAppSelector } from '../../store/hooks';
import { logError } from '../../utils/errorLogger';
import AddEventToSeriesModal from './AddEventToSeriesModal';

interface SeriesEventsSectionProps {
  currentEvent: Event;
  onEventView?: (event: Event) => void;
}

const SeriesEventsSection: React.FC<SeriesEventsSectionProps> = ({
  currentEvent,
  onEventView
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.ui);
  const [seriesData, setSeriesData] = useState<{ series: EventSeries; events: Event[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPastEvents, setShowPastEvents] = useState(true);
  const [navigatingToEvent, setNavigatingToEvent] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);

  const fetchSeriesEvents = useCallback(async () => {
    if (!currentEvent.series?.id) return;

    try {
      setLoading(true);
      const data = await eventAPI.getSeriesEvents(currentEvent.series.id);
      setSeriesData(data);
      setError(null);
    } catch (err) {
      logError('Failed to fetch series events', err);
      setError('Failed to load series events');
    } finally {
      setLoading(false);
    }
  }, [currentEvent.series?.id]);

  useEffect(() => {
    fetchSeriesEvents();
  }, [fetchSeriesEvents]);

  if (!currentEvent.series?.id) {
    return null; // Not part of a series
  }

  if (loading) {
    return (
      <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-center py-6 sm:py-8">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
          <span className={`ml-3 text-sm sm:text-base ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>Loading series events...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg shadow-sm border p-4 sm:p-6 ${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="text-center py-6 sm:py-8">
          <p className={`text-sm sm:text-base ${
            theme === 'dark' ? 'text-red-400' : 'text-red-600'
          }`}>{error}</p>
        </div>
      </div>
    );
  }

  if (!seriesData || seriesData.events.length <= 1) {
    return null; // No other events in series
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isCurrentEvent = (event: Event) => event.id === currentEvent.id;

  const isEarliestEvent = (event: Event) => {
    if (!seriesData) return false;
    
    // Find the earliest event by comparing dates and times
    const sortedEvents = [...seriesData.events].sort((a, b) => {
      const dateA = new Date(`${a.event_date}T${a.event_time}`);
      const dateB = new Date(`${b.event_date}T${b.event_time}`);
      return dateA.getTime() - dateB.getTime();
    });
    
    return sortedEvents.length > 0 && sortedEvents[0].id === event.id;
  };

  const getUpcomingEvent = () => {
    if (!seriesData) return null;
    
    const now = new Date();
    const upcomingEvents = seriesData.events
      .filter(event => {
        const eventDateTime = new Date(`${event.event_date}T${event.event_time}`);
        return eventDateTime > now;
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.event_date}T${a.event_time}`);
        const dateB = new Date(`${b.event_date}T${b.event_time}`);
        return dateA.getTime() - dateB.getTime();
      });
    
    return upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  };

  const isUpcomingEvent = (event: Event) => {
    const upcomingEvent = getUpcomingEvent();
    return upcomingEvent ? upcomingEvent.id === event.id : false;
  };

  const handleEventClick = (event: Event) => {
    // Toggle expanded view for inline details
    if (expandedEventId === event.id) {
      setExpandedEventId(null);
    } else {
      setExpandedEventId(event.id);
    }
  };

  const handleNavigateToEvent = (event: Event) => {
    // Set loading state for visual feedback
    setNavigatingToEvent(event.id);
    
    if (onEventView) {
      // Small delay to show loading state
      setTimeout(() => {
        onEventView(event);
        setNavigatingToEvent(null);
      }, 150);
    } else {
      // Navigate to event view page
      setTimeout(() => {
        const eventUrl = generateEventViewUrl(event.id, event.title, event.event_date, user?.role, event.slug);
        window.location.href = eventUrl;
      }, 150);
    }
  };

  return (
    <div className={`rounded-xl border overflow-hidden ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700' 
        : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
    }`}>
      {/* Header */}
      <div className={`border-b p-4 sm:p-6 ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700' 
          : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mr-3 ${
              theme === 'dark' 
                ? 'bg-gray-700 border border-gray-600' 
                : 'bg-gray-100 border border-gray-300'
            }`}>
              <CalendarDays className={`h-5 w-5 sm:h-6 sm:w-6 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`text-base sm:text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                "{seriesData.series.name}" Series
              </h3>
              <p className={`text-xs sm:text-sm mt-1 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {seriesData.events.length} events in this series • You're viewing Event #{currentEvent.series_position || '?'}
              </p>
            </div>
          </div>
          
          <div className={`flex items-center px-3 py-2 rounded-full text-xs sm:text-sm font-medium border ${
            theme === 'dark' 
              ? 'bg-gray-700 border-gray-600 text-gray-300' 
              : 'bg-gray-100 border-gray-300 text-gray-700'
          }`}>
            <CalendarDays className="h-4 w-4 mr-1.5" />
            <span className="whitespace-nowrap">Event Series</span>
          </div>
        </div>
      </div>

      {/* Timeline Controls */}
      <div className={`px-3 sm:px-6 py-3 sm:py-4 border-b ${
        theme === 'dark' 
          ? 'border-gray-700 bg-gray-800' 
          : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-6">
            {/* Past Events Toggle */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowPastEvents(!showPastEvents)}
                className={`flex items-center space-x-2 text-sm sm:text-base font-medium transition-colors min-h-[44px] px-2 ${
                  theme === 'dark' 
                    ? 'text-gray-300 hover:text-white' 
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                {showPastEvents ? (
                  <ToggleRight className={`h-5 w-5 sm:h-6 sm:w-6 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`} />
                ) : (
                  <ToggleLeft className={`h-5 w-5 sm:h-6 sm:w-6 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                )}
                <span>Show Past Events</span>
              </button>
            </div>
            
            {/* Timeline Statistics */}
            <div className={`flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <span>
                {seriesData.events.filter(e => new Date(`${e.event_date}T${e.event_time}`) < new Date()).length} Past
              </span>
              <span className="hidden sm:inline">•</span>
              <span>
                {seriesData.events.filter(e => new Date(`${e.event_date}T${e.event_time}`) > new Date()).length} Upcoming
              </span>
              <span className="hidden md:inline">•</span>
              <span className={`font-medium hidden md:inline ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Click events to expand details
              </span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {/* Add Event Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowAddEventModal(true)}
              className="flex items-center justify-center space-x-2 text-xs sm:text-sm min-h-[44px]"
            >
              <Plus className="h-4 w-4" />
              <span>Add Event</span>
            </Button>
            
            {/* Upcoming Event Indicator */}
            {getUpcomingEvent() && (
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-full text-xs sm:text-sm font-medium border min-h-[44px] ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-gray-300' 
                  : 'bg-gray-100 border-gray-300 text-gray-700'
              }`}>
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                }`}></div>
                <span className="truncate">Next: {getUpcomingEvent()?.title}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Events Timeline */}
      <div className="p-3 sm:p-6">
        <div className="relative">
          {/* Timeline line */}
          <div className={`absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 sm:w-1 ${
            theme === 'dark' 
              ? 'bg-gradient-to-b from-gray-600 to-gray-500' 
              : 'bg-gradient-to-b from-gray-300 to-gray-400'
          }`}></div>
          
          <div className="space-y-4 sm:space-y-6">
            {seriesData.events
              .filter(event => {
                const isPast = new Date(`${event.event_date}T${event.event_time}`) < new Date();
                return showPastEvents || !isPast;
              })
              .map((event, index) => {
                const isPast = new Date(`${event.event_date}T${event.event_time}`) < new Date();
                const isCurrent = isCurrentEvent(event);
                const isEarliest = isEarliestEvent(event);
                const isUpcoming = isUpcomingEvent(event);
              
              return (
                <div key={event.id} className="relative">
                  {/* Timeline node */}
                  <div className={`absolute left-2 sm:left-6 w-5 h-5 sm:w-4 sm:h-4 rounded-full border-2 ${
                    isCurrent 
                      ? 'bg-mirsal-green border-mirsal-green shadow-lg ring-2 sm:ring-4 ring-mirsal-green ring-opacity-30' 
                      : isUpcoming
                      ? 'bg-gradient-to-br from-mirsal-green to-green-600 border-mirsal-green shadow-lg ring-2 sm:ring-4 ring-mirsal-green ring-opacity-20 animate-pulse'
                      : isEarliest
                      ? 'bg-gradient-to-br from-mirsal-black to-gray-800 border-mirsal-black shadow-lg ring-2 sm:ring-4 ring-gray-300'
                      : isPast
                      ? 'bg-gray-400 border-gray-400'
                      : 'bg-mirsal-green border-mirsal-green'
                  }`}>
                    {isCurrent && (
                      <div className="absolute inset-0.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  
                  {/* Event card */}
                  <div className="ml-10 sm:ml-16">
                    <div
                      className={`relative group rounded-xl border transition-all duration-300 ${
                        navigatingToEvent === event.id
                          ? (theme === 'dark' 
                              ? 'border-gray-500 bg-gray-700 bg-opacity-50 shadow-lg ring-2 ring-gray-500 animate-pulse' 
                              : 'border-gray-400 bg-gray-100 bg-opacity-50 shadow-lg ring-2 ring-gray-400 animate-pulse')
                          : isCurrent
                          ? (theme === 'dark' 
                              ? 'border-gray-600 bg-gray-800 shadow-lg ring-1 ring-gray-600' 
                              : 'border-gray-300 bg-white shadow-lg ring-1 ring-gray-300')
                          : isUpcoming
                          ? (theme === 'dark' 
                              ? 'border-gray-600 bg-gradient-to-br from-gray-800 to-gray-700 shadow-md ring-1 ring-gray-600 ring-opacity-30 hover:shadow-lg hover:shadow-gray-600/20 cursor-pointer' 
                              : 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 shadow-md ring-1 ring-gray-300 ring-opacity-30 hover:shadow-lg hover:shadow-gray-400/20 cursor-pointer')
                          : isEarliest
                          ? (theme === 'dark' 
                              ? 'border-gray-500 bg-gradient-to-br from-gray-800 to-gray-700 shadow-md ring-1 ring-gray-600 hover:shadow-lg hover:shadow-gray-500/20 cursor-pointer' 
                              : 'border-gray-400 bg-gradient-to-br from-gray-50 to-gray-100 shadow-md ring-1 ring-gray-300 hover:shadow-lg hover:shadow-gray-400/20 cursor-pointer')
                          : (theme === 'dark' 
                              ? 'border-gray-600 bg-gray-800 hover:border-gray-500 hover:shadow-md hover:shadow-gray-500/10 cursor-pointer' 
                              : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md hover:shadow-gray-400/10 cursor-pointer')
                      } ${!isCurrent && navigatingToEvent !== event.id ? 'hover:-translate-y-1 hover:scale-[1.02]' : ''}`}
                      onClick={() => !isCurrent && handleEventClick(event)}
                    >
                      <div className="p-4 sm:p-5">
                        {/* Event header */}
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center mb-2">
                              <span className={`inline-flex items-center justify-center w-8 h-8 sm:w-7 sm:h-7 text-sm sm:text-base font-bold rounded-lg mr-2 sm:mr-3 ${
                                isCurrent 
                                  ? 'bg-mirsal-green text-mirsal-black' 
                                  : isUpcoming
                                  ? 'bg-gradient-to-br from-mirsal-green to-green-600 text-white shadow-md'
                                  : isEarliest
                                  ? 'bg-gradient-to-br from-mirsal-black to-gray-800 text-white shadow-md'
                                  : isPast
                                  ? 'bg-gray-400 text-white'
                                  : 'bg-green-100 text-mirsal-green'
                              }`}>
                                {index + 1}
                              </span>
                              <h4 className={`font-semibold text-base sm:text-lg ${
                                theme === 'dark' 
                                  ? (isCurrent ? 'text-white' : 'text-white') 
                                  : (isCurrent ? 'text-mirsal-black' : 'text-gray-900')
                              }`}>
                                {event.title}
                              </h4>
                            </div>
                          </div>
                          
                          {/* Click indicator for non-current events */}
                          {!isCurrent && (
                            <div className={`flex items-center space-x-2 transition-colors duration-300 ${
                              theme === 'dark' 
                                ? 'text-gray-500 group-hover:text-gray-300' 
                                : 'text-gray-400 group-hover:text-gray-600'
                            }`}>
                              <div className="flex items-center space-x-1 text-xs sm:text-sm font-medium">
                                <span className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                                  {expandedEventId === event.id ? 'Hide' : 'View'}
                                </span>
                                {expandedEventId === event.id ? (
                                  <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-300" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-300" />
                                )}
                              </div>
                              {/* Separate navigate button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNavigateToEvent(event);
                                }}
                                className={`opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 p-2 sm:p-1 rounded min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                                  theme === 'dark' 
                                    ? 'hover:bg-gray-600 hover:text-white' 
                                    : 'hover:bg-gray-200 hover:text-gray-900'
                                }`}
                                title="Navigate to this event"
                              >
                                {navigatingToEvent === event.id ? (
                                  <div className="w-4 h-4 sm:w-3 sm:h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <ChevronRight className="h-4 w-4 sm:h-3 sm:w-3" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Status badges */}
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {isCurrent && (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium border ${
                              theme === 'dark' 
                                ? 'bg-gray-700 border-gray-600 text-gray-300' 
                                : 'bg-gray-100 border-gray-300 text-gray-700'
                            }`}>
                              <span className="hidden sm:inline">{isEarliest ? 'Currently Viewing • First Event' : isUpcoming ? 'Currently Viewing • Next Up' : 'Currently Viewing'}</span>
                              <span className="sm:hidden">Viewing</span>
                            </span>
                          )}
                          {isUpcoming && !isCurrent && (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium border shadow-sm ${
                              theme === 'dark' 
                                ? 'bg-gray-700 border-gray-600 text-gray-300' 
                                : 'bg-gray-100 border-gray-300 text-gray-700'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full mr-1.5 animate-pulse ${
                                theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                              }`}></div>
                              Next Event
                            </span>
                          )}
                          {isEarliest && !isCurrent && !isUpcoming && (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium border shadow-sm ${
                              theme === 'dark' 
                                ? 'bg-gray-800 border-gray-700 text-gray-300' 
                                : 'bg-gray-200 border-gray-400 text-gray-700'
                            }`}>
                              First Event
                            </span>
                          )}
                          {isPast && !isCurrent && !isEarliest && !isUpcoming && (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium border ${
                              theme === 'dark' 
                                ? 'bg-gray-700 border-gray-600 text-gray-400' 
                                : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}>
                              Past Event
                            </span>
                          )}
                          {!isPast && !isCurrent && !isEarliest && !isUpcoming && (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium border ${
                              theme === 'dark' 
                                ? 'bg-gray-700 border-gray-600 text-gray-300' 
                                : 'bg-gray-100 border-gray-300 text-gray-700'
                            }`}>
                              Upcoming
                            </span>
                          )}
                        </div>

                        {/* Event details grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                          <div className={`flex items-center text-sm sm:text-base ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            <div className={`w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${
                              theme === 'dark' 
                                ? 'bg-gray-700 border border-gray-600' 
                                : 'bg-gray-100 border border-gray-300'
                            }`}>
                              <Clock className={`h-5 w-5 sm:h-4 sm:w-4 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`} />
                            </div>
                            <div className="min-w-0">
                              <p className={`font-medium text-base sm:text-sm ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>{formatDate(event.event_date)}</p>
                              <p className={`text-sm sm:text-xs ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>{formatTime(event.event_time)}</p>
                            </div>
                          </div>
                          
                          <div className={`flex items-center text-sm sm:text-base ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            <div className={`w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${
                              theme === 'dark' 
                                ? 'bg-gray-700 border border-gray-600' 
                                : 'bg-gray-100 border border-gray-300'
                            }`}>
                              <MapPin className={`h-5 w-5 sm:h-4 sm:w-4 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`font-medium text-base sm:text-sm truncate ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>{event.location}</p>
                              <p className={`text-sm sm:text-xs ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>Location</p>
                            </div>
                          </div>
                          
                          <div className={`flex items-center text-sm sm:text-base ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            <div className={`w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${
                              theme === 'dark' 
                                ? 'bg-gray-700 border border-gray-600' 
                                : 'bg-gray-100 border border-gray-300'
                            }`}>
                              <Users className={`h-5 w-5 sm:h-4 sm:w-4 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`} />
                            </div>
                            <div className="min-w-0">
                              <p className={`font-medium text-base sm:text-sm ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                {event.accepted_count || 0}
                                {event.guest_limit && `/${event.guest_limit}`}
                              </p>
                              <p className={`text-sm sm:text-xs ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {event.pending_count && event.pending_count > 0 
                                  ? `${event.pending_count} pending`
                                  : 'guests'
                                }
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Expandable event details */}
                        {expandedEventId === event.id && (
                          <div className={`mt-4 pt-4 border-t ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                          }`}>
                            <div className={`rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4 ${
                              theme === 'dark' 
                                ? 'bg-gradient-to-br from-gray-800 to-gray-700' 
                                : 'bg-gradient-to-br from-gray-50 to-white'
                            }`}>
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                <h5 className={`font-semibold text-base sm:text-lg ${
                                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>Event Details</h5>
                                <span className={`text-xs sm:text-sm ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  {formatDate(event.event_date)} • {formatTime(event.event_time)}
                                </span>
                              </div>

                              {/* Event description */}
                              {event.description && (
                                <div className={`rounded-lg p-3 sm:p-4 border ${
                                  theme === 'dark' 
                                    ? 'bg-gray-800 border-gray-700' 
                                    : 'bg-white border-gray-200'
                                }`}>
                                  <h6 className={`font-medium mb-2 flex items-center text-sm sm:text-base ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    <div className={`w-2 h-2 rounded-full mr-2 ${
                                      theme === 'dark' ? 'bg-gray-400' : 'bg-gray-500'
                                    }`}></div>
                                    Description
                                  </h6>
                                  <p className={`text-sm sm:text-base leading-relaxed ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                  }`}>{event.description}</p>
                                </div>
                              )}

                              {/* Event settings */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className={`rounded-lg p-3 sm:p-4 border ${
                                  theme === 'dark' 
                                    ? 'bg-gray-800 border-gray-700' 
                                    : 'bg-white border-gray-200'
                                }`}>
                                  <h6 className={`font-medium mb-2 flex items-center text-sm sm:text-base ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    <Users className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 ${
                                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`} />
                                    Guest Settings
                                  </h6>
                                  <div className="space-y-2 text-sm sm:text-base">
                                    <div className="flex justify-between">
                                      <span className={`${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                      }`}>Guest Limit:</span>
                                      <span className={`font-medium ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                      }`}>{event.guest_limit || 'Unlimited'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className={`${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                      }`}>Accepted:</span>
                                      <span className={`font-medium ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                      }`}>{event.accepted_count || 0}</span>
                                    </div>
                                    {event.pending_count && event.pending_count > 0 && (
                                      <div className="flex justify-between">
                                        <span className={`${
                                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                        }`}>Pending:</span>
                                        <span className={`font-medium ${
                                          theme === 'dark' ? 'text-orange-400' : 'text-orange-500'
                                        }`}>{event.pending_count}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className={`rounded-lg p-3 sm:p-4 border ${
                                  theme === 'dark' 
                                    ? 'bg-gray-800 border-gray-700' 
                                    : 'bg-white border-gray-200'
                                }`}>
                                  <h6 className={`font-medium mb-2 flex items-center text-sm sm:text-base ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    <Clock className={`h-4 w-4 sm:h-5 sm:w-5 mr-2 ${
                                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`} />
                                    Event Status
                                  </h6>
                                  <div className="space-y-2 text-sm sm:text-base">
                                    <div className="flex justify-between">
                                      <span className={`${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                      }`}>Status:</span>
                                      <span className={`font-medium px-2 py-1 rounded text-xs ${
                                        event.status === 'active' 
                                          ? (theme === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                                        : event.status === 'pending' 
                                          ? (theme === 'dark' ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-700')
                                        : event.status === 'rejected' 
                                          ? (theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                                        : event.status === 'canceled' 
                                          ? (theme === 'dark' ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')
                                        : event.status === 'archived' 
                                          ? (theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                                        : event.status === 'rescheduled' 
                                          ? (theme === 'dark' ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700')
                                        : (theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                                      }`}>
                                        {event.status?.charAt(0).toUpperCase() + event.status?.slice(1) || 'Draft'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className={`${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                      }`}>Editable Until:</span>
                                      <span className={`font-medium ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                      }`}>{event.editable_until || 'Event time'}</span>
                                    </div>
                                    {event.time_until_event && (
                                      <div className="flex justify-between">
                                        <span className={`${
                                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                        }`}>Time Until:</span>
                                        <span className={`font-medium ${
                                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>{event.time_until_event}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Action buttons */}
                              <div className={`flex items-center justify-end pt-3 border-t ${
                                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                              }`}>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigateToEvent(event);
                                  }}
                                  disabled={navigatingToEvent === event.id}
                                  className="min-h-[44px] text-sm sm:text-base"
                                >
                                  {navigatingToEvent === event.id ? (
                                    <>
                                      <div className="w-4 h-4 sm:w-3 sm:h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                      Loading...
                                    </>
                                  ) : (
                                    <>
                                      <Eye className="h-4 w-4 sm:h-3 sm:w-3 mr-2" />
                                      Open Event
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hidden events notification */}
        {!showPastEvents && seriesData.events.filter(e => new Date(`${e.event_date}T${e.event_time}`) < new Date()).length > 0 && (
          <div className={`mt-4 sm:mt-6 p-3 sm:p-4 border rounded-lg ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2 text-sm sm:text-base text-gray-600">
                <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span>
                  {seriesData.events.filter(e => new Date(`${e.event_date}T${e.event_time}`) < new Date()).length} past event(s) hidden
                </span>
              </div>
              <button
                onClick={() => setShowPastEvents(true)}
                className={`text-sm sm:text-base text-mirsal-green hover:text-mirsal-black font-medium min-h-[44px] px-4 py-2 ${
                  theme === 'dark' 
                    ? 'hover:bg-gray-700 rounded-lg' 
                    : 'hover:bg-gray-100 rounded-lg'
                }`}
              >
                Show All Events
              </button>
            </div>
          </div>
        )}

        {/* Series description */}
        {seriesData.series.description && (
          <div className={`mt-6 sm:mt-8 p-4 sm:p-5 rounded-lg border shadow-sm ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-mirsal-green'
          }`}>
            <div className="flex items-start">
              <div className={`w-10 h-10 sm:w-8 sm:h-8 bg-mirsal-green rounded-lg flex items-center justify-center mr-3 mt-1 flex-shrink-0 ${
                theme === 'dark' ? 'bg-mirsal-green/80' : ''
              }`}>
                <CalendarDays className="h-5 w-5 sm:h-4 sm:w-4 text-mirsal-black" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className={`font-medium text-base sm:text-lg mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-mirsal-black'
                }`}>About This Series</h4>
                <p className={`text-sm sm:text-base leading-relaxed ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {seriesData.series.description}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Event to Series Modal */}
      {currentEvent.series?.id && (
        <AddEventToSeriesModal
          isOpen={showAddEventModal}
          onClose={() => setShowAddEventModal(false)}
          seriesId={currentEvent.series.id}
          mainEventDate={
            seriesData?.events?.find(e => e.is_main_event)?.event_date ||
            seriesData?.events?.sort((a, b) => {
              const dateA = new Date(`${a.event_date}T${a.event_time}`);
              const dateB = new Date(`${b.event_date}T${b.event_time}`);
              return dateA.getTime() - dateB.getTime();
            })[0]?.event_date ||
            currentEvent.event_date
          }
          onSuccess={() => {
            fetchSeriesEvents();
            setShowAddEventModal(false);
          }}
        />
      )}
    </div>
  );
};

export default SeriesEventsSection;
